import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchContentItem } from '@/shared/api/contentItem';
import PostPreviewCard from '@/features/posts/components/PostPreviewCard';

const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN;

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const POST_PATTERN = new RegExp(`${APP_DOMAIN}/post/(${UUID})`, 'i');
const WORLD_PATTERN = new RegExp(`${APP_DOMAIN}/world-view/(${UUID})`, 'i');

/**
 * 偵測訊息中第一個站內連結
 * @param {string} text
 * @returns {{ type: 'post'|'world', uuid: string, url: string } | null}
 */
export const detectLink = (text) => {
  const postMatch = text.match(POST_PATTERN);
  if (postMatch) return { type: 'post', uuid: postMatch[1], url: postMatch[0] };
  const worldMatch = text.match(WORLD_PATTERN);
  if (worldMatch) return { type: 'world', uuid: worldMatch[1], url: worldMatch[0] };
  return null;
};

/**
 * 偵測訊息中第一個站內連結，並渲染 PostPreviewCard
 * @param {{ content: string, isSelf: boolean }} props
 */
const ChatLinkPreview = ({ content, isSelf = false }) => {
  const { t } = useTranslation();
  const idolId = useSelector((state) => state.user.idolId);

  const [previewData, setPreviewData] = useState(null);
  const [contentType, setContentType] = useState(null); // 'post' | 'world'
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchPreview = useCallback(async () => {
    const link = detectLink(content);
    if (!link) return;

    setLoading(true);
    setFailed(false);
    setPreviewData(null);
    setContentType(link.type);

    try {
      const result = await fetchContentItem({
        contentType: link.type,
        uuid: link.uuid,
        idolId,
      });
      if (result.status_code === 'SYSTEM_1000') {
        setPreviewData(result.data);
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [content, idolId]);

  useEffect(() => {
    if (!content) return;
    fetchPreview();
  }, [fetchPreview]);

  if (!detectLink(content)) return null;

  const bubbleClass = `${isSelf ? 'msg-me' : 'msg-other'} rounded-4 px-3 py-2`;

  if (loading) return (
    <div className={bubbleClass}>
      <ReactLoading type="spin" color="#ff7bac" height={24} width={24} />
    </div>
  );
  if (failed) return (
    <div className={bubbleClass}>
      <p className="mb-0 text-gray">
        <FontAwesomeIcon icon={['fas', contentType === 'world' ? 'earth-americas' : 'address-card']} className="me-1" />
        {t('load_failed', { type: t(contentType === 'world' ? 'worldview' : 'post') })}
      </p>
    </div>
  );
  if (!previewData) return null;

  return (
    <PostPreviewCard
      data={previewData}
      contentType={contentType}
    />
  );
};

export default ChatLinkPreview;
